<script lang="ts">
  import { Copy, ExternalLink, Globe, MapPin, Phone } from "@lucide/svelte";
  import { toast } from "svelte-sonner";
  import type { LngLat } from "../../lib/geo/types.ts";
  import type { ContactLink } from "../../lib/osm/contact.ts";
  import type { OsmProfile } from "../../lib/osm.ts";
  import FacebookIcon from "../icons/FacebookIcon.svelte";
  import InstagramIcon from "../icons/InstagramIcon.svelte";
  import WikipediaIcon from "../icons/WikipediaIcon.svelte";
  import { Button } from "../ui/button/index.ts";
  import * as Item from "../ui/item/index.ts";

  let {
    details,
    lngLat,
  }: {
    details: OsmProfile;
    lngLat: LngLat;
  } = $props();

  const detailItemClass = "gap-3 px-4 py-1.5";

  type DetailLinkIcon =
    | typeof Globe
    | typeof Phone
    | typeof WikipediaIcon
    | typeof FacebookIcon
    | typeof InstagramIcon;

  interface DetailLink extends ContactLink {
    Icon: DetailLinkIcon;
    copyText: string | undefined;
    copyLabel: string;
  }

  async function copyValue(value: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  }
</script>

<script lang="ts" module>
  // Defer the parser and timezone lookup until a selected place has this tag.
  let openingHoursDetails:
    | Promise<typeof import("./OpeningHoursDetails.svelte")>
    | undefined;

  function loadOpeningHoursDetails() {
    return openingHoursDetails ??= import("./OpeningHoursDetails.svelte").catch(
      (error) => {
        console.error(
          "Dynamic import of OpeningHoursDetails component failed",
          error,
        );
        throw error;
      },
    );
  }
</script>

{#snippet copyAction(
  copyText: string | undefined,
  label: string,
  ariaLabel: string,
)}
  {#if copyText}
    <Item.Actions>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        class="text-muted-foreground hover:text-foreground"
        aria-label={ariaLabel}
        onclick={() => void copyValue(copyText, label)}
      >
        <Copy />
      </Button>
    </Item.Actions>
  {/if}
{/snippet}

{#snippet detailLink(link: DetailLink)}
  {@const Icon = link.Icon}
  <Item.Root size="sm" class={detailItemClass}>
    <Item.Media>
      <Icon class="size-4 text-muted-foreground" />
    </Item.Media>
    <Item.Content class="min-w-0">
      <a
        class="inline-flex min-w-0 max-w-full items-center text-foreground no-underline hover:underline"
        href={link.href}
        target={link.href.startsWith("tel:") ? undefined : "_blank"}
        rel={link.href.startsWith("tel:") ? undefined : "noopener noreferrer"}
      >
        <Item.Title class="min-w-0 max-w-full font-normal">
          <span class="min-w-0 truncate">
            {link.label}
          </span>
          {#if !link.href.startsWith("tel:")}
            <ExternalLink class="size-3 shrink-0 text-muted-foreground" />
          {/if}
        </Item.Title>
      </a>
    </Item.Content>
    {@render copyAction(
      link.copyText,
      link.copyLabel,
      `Copy ${link.copyLabel.toLowerCase()}`,
    )}
  </Item.Root>
{/snippet}

<section class="flex flex-col gap-4">
  <h3 class="px-4 text-xs font-semibold uppercase text-muted-foreground">
    Details
  </h3>

  <Item.Group class="gap-1">
    {#if details.addressLines.length > 0}
      <Item.Root size="sm" class={detailItemClass}>
        <Item.Media class="mt-0.5 self-start">
          <MapPin class="size-4 text-muted-foreground" />
        </Item.Media>
        <Item.Content class="min-w-0">
          <Item.Title class="block font-normal">
            {#each details.addressLines as line}
              <span class="block">{line}</span>
            {/each}
          </Item.Title>
        </Item.Content>
        {@render copyAction(
          details.addressLines.join("\n"),
          "Address",
          "Copy address",
        )}
      </Item.Root>
    {/if}

    {#if details.openingHours}
      {#await loadOpeningHoursDetails() then module}
        {@const OpeningHoursDetails = module.default}
        <OpeningHoursDetails
          input={{ tagValue: details.openingHours, lngLat }}
        />
      {:catch}
        <!-- The loader logs the error; the raw value remains in the OSM tag table. -->
      {/await}
    {/if}

    {#if details.websiteLink}
      {@render detailLink({
        ...details.websiteLink,
        Icon: Globe,
        copyText: details.websiteLink.href,
        copyLabel: "Website",
      })}
    {/if}
    {#if details.phoneLink}
      {@render detailLink({
        ...details.phoneLink,
        Icon: Phone,
        copyText: details.phoneLink.label,
        copyLabel: "Phone",
      })}
    {/if}
    {#if details.wikipediaLink}
      {@render detailLink({
        ...details.wikipediaLink,
        Icon: WikipediaIcon,
        copyText: undefined,
        copyLabel: "Wikipedia",
      })}
    {/if}
    {#if details.facebookLink}
      {@render detailLink({
        ...details.facebookLink,
        Icon: FacebookIcon,
        copyText: undefined,
        copyLabel: "Facebook",
      })}
    {/if}
    {#if details.instagramLink}
      {@render detailLink({
        ...details.instagramLink,
        Icon: InstagramIcon,
        copyText: undefined,
        copyLabel: "Instagram",
      })}
    {/if}
  </Item.Group>
</section>
